import { lazy, Suspense, useContext, useLayoutEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import ConfigService from "@/services/ConfigService";
import useIsPromoCompleted from "@/widgets/casinoWidget/hooks/useIsPromoCompleted";
import { ConfigProvider } from "@/providers/ConfigProvider";
import { GamesProvider } from "@/providers/GamesProvider";
import { AuthContext, AuthContextProps } from "@/providers/AuthProvider";
import { CurrencyProvider } from "@/providers/CurrencyProvider";
import { useCasinoData, useOptInStatus, useUserProgress } from "../../../casino.api";
import {
  getIsOptedIn,
  getShouldLoadOptInStatus,
  getShouldLoadUserProgress,
} from "../../../CasinoWidget.helpers";
import CasinoLoading from "@/components/casinoLoading/CasinoLoading";
import JackpotCardsLoader from "./JackpotCardsLoader";
import EmptyState from "@/components/emptyState/EmptyState";
import emptyStateImg from "@/assets/img/emptyCasinoIcon.png";
import { PromotionStatusEnum } from "@/widgets/pickemWidget/pickem.types";

const JackpotCardsWidget = lazy(() => import("./JackpotCardsWidget"));
const JackpotFullscreenWidget = lazy(() => import("./JackpotFullscreenWidget"));
const MultiJackpotCardsWidget = lazy(() => import("./MultiJackpotCardsWidget"));
const MultiJackpotFullscreenWidget = lazy(() => import("./MultiJackpotFullscreenWidget"));

type JackpotIframeMode = "cards" | "fullscreen";

interface JackpotIframeGuardProps {
  mode: JackpotIframeMode;
}

/**
 * Entry point guard for dual-iframe jackpot architecture.
 * Wraps the cards-only or fullscreen widget with required providers and data loading.
 */
const JackpotIframeGuard = ({ mode }: JackpotIframeGuardProps) => {
  const promoId = ConfigService.getPromoId();
  const gameId = ConfigService.getGameId();
  const providerId = ConfigService.getProviderId();

  useLayoutEffect(() => {
    document.documentElement.classList.add("jackpot-iframe");
    return () => document.documentElement.classList.remove("jackpot-iframe");
  }, []);

  // Multi-jackpot mode: both gameId and providerId without promoId
  if (gameId && providerId && !promoId) {
    const Loader = mode === "cards" ? JackpotCardsLoader : CasinoLoading;
    return (
      <Suspense fallback={<Loader />}>
        {mode === "cards" ? (
          <MultiJackpotCardsWidget gameId={gameId} providerId={providerId} />
        ) : (
          <MultiJackpotFullscreenWidget gameId={gameId} providerId={providerId} />
        )}
      </Suspense>
    );
  }

  if (!promoId) {
    return (
      <EmptyState
        img={emptyStateImg}
        title="Initialization Failed"
        text="SDK initialization failed. Missing promoId, gameId or providerId parameter."
      />
    );
  }

  const Loader = mode === "cards" ? JackpotCardsLoader : CasinoLoading;

  return (
    <ConfigProvider>
      <GamesProvider>
        <Suspense fallback={<Loader />}>
          <JackpotIframeContent mode={mode} />
        </Suspense>
      </GamesProvider>
    </ConfigProvider>
  );
};

const JackpotIframeContent = ({ mode }: { mode: JackpotIframeMode }) => {
  const Loader = mode === "cards" ? JackpotCardsLoader : CasinoLoading;
  const authContext = useContext<AuthContextProps>(AuthContext);
  const { t } = useTranslation();
  const hasRenderedRef = useRef(false);

  const { data: casinoData, isLoading: isCasinoLoading, isError: isCasinoError } = useCasinoData();
  const isCompleted = useIsPromoCompleted(casinoData?.endDate);

  const shouldLoadOptIn = getShouldLoadOptInStatus({
    isBadgeSource: false,
    hasOptIn: !!casinoData?.optIn,
    isLoggedIn: authContext.isLoggedIn,
    promoRef: casinoData?.promoRef,
  });

  const { data: optInStatus, isLoading: isOptInLoading } = useOptInStatus(
    authContext,
    shouldLoadOptIn,
    casinoData?.promoRef
  );

  const isOptedIn = getIsOptedIn(optInStatus, false);
  const hasOptIn = !!casinoData?.optIn;

  const shouldLoadUserProgress = getShouldLoadUserProgress({
    isLoggedIn: authContext.isLoggedIn,
    isBadgeSource: false,
    promoRef: casinoData?.promoRef,
    hasOptIn,
    isOptedIn,
  });

  const { data: userProgressData, isLoading: isProgressLoading } = useUserProgress(
    authContext,
    shouldLoadUserProgress,
    casinoData?.promoRef
  );

  const shouldShowLoader =
    isCasinoLoading || isOptInLoading || (isProgressLoading && !hasRenderedRef.current);

  if (shouldShowLoader) return mode === "cards" ? <JackpotCardsLoader /> : <CasinoLoading />;
  if (isCasinoError || !casinoData) {
    return (
      <EmptyState img={emptyStateImg} title={t("emptyState.oops")} text={t("casinoWidget.error")} />
    );
  }

  hasRenderedRef.current = true;
  const userProgress = shouldLoadUserProgress ? userProgressData : null;

  if (isCompleted) return null;

  // Paused jackpots render nothing — empty cards strip, no clickable carousel.
  if (casinoData.status === PromotionStatusEnum.Paused) return null;

  return (
    <CurrencyProvider widgetData={casinoData}>
      <Suspense fallback={<Loader />}>
        {mode === "cards" ? (
          <JackpotCardsWidget casinoData={casinoData} userProgress={userProgress} />
        ) : (
          <JackpotFullscreenWidget casinoData={casinoData} userProgress={userProgress} />
        )}
      </Suspense>
    </CurrencyProvider>
  );
};

export default JackpotIframeGuard;
